import type React from 'react';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import { ModalState } from '@/components/header/types';

const modalRoot = document.getElementById('root');

export interface GenericModalProps {
  modalStateChangeHandler: (modal: ModalState) => void;
  isVisible: boolean;
}

interface ModalProps extends GenericModalProps {
  title: string;
  children: ReactNode;
}

export interface DialogTitleProps {
  id: string;
  children?: ReactNode;
  onClose: () => void;
}

export const ModalLayout: React.FC<ModalProps> = (props) => {
  const modalContainerRef = useRef<HTMLDivElement>(document.createElement('div'));

  useEffect(() => {
    const currentModalContainer = modalContainerRef.current;
    if (modalRoot) {
      modalRoot.appendChild(currentModalContainer);
    }

    return (): void => void modalRoot?.removeChild(currentModalContainer);
  }, []);
  return ReactDOM.createPortal(
    <Dialog open={props.isVisible} maxWidth="md">
      <ModalContainer>
        <BootstrapDialogTitle
          id="customized-dialog-title"
          onClose={(): void => {
            props.modalStateChangeHandler(ModalState.None);
          }}
        >
          {props.title}
        </BootstrapDialogTitle>
        {props.children}
      </ModalContainer>
    </Dialog>,
    modalContainerRef.current
  );
};

function BootstrapDialogTitle(props: DialogTitleProps): ReactNode {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2, fontWeight: '600' }} {...other}>
      {children}
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'rgba(170, 170, 170, 0.6)'
        }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
  );
}

const ModalContainer = styled.div`
  width: 620px;
  padding-bottom: 10px;
  & a {
    color: ${({ theme }): string => (theme.mode === 'light' ? '#0000EE' : '#00BFFF')};
  }
`;
